// ─── Helpers partagés des écrans « Routeurs MikroTik » ─────────────
// Liste, fiche, modales d'ajout / de mise à jour et panneau de sauvegardes.

import { api } from './api.jsx';

/**
 * Appel d'API qui garde le corps des erreurs (`api.post` le jette sur un 4xx,
 * or c'est lui qui porte `{ error }` et, sur 422/409, le résultat du test).
 */
export async function mikrotikCall(path, method = 'GET', body = undefined) {
  const res = await api.fetch(path, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  let json = null;
  try {
    const t = await res.text();
    json = t ? JSON.parse(t) : null;
  } catch {
    /* corps non JSON */
  }
  return { ok: res.ok, status: res.status, json };
}

export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

/** Octets lisibles, base 1024 (« 1,5 Go »). */
export function fmtBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v < 1024) return v + ' o';
  const units = ['Kio', 'Mio', 'Gio', 'Tio', 'Pio'];
  let x = v / 1024;
  let i = 0;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return x.toFixed(x >= 100 ? 0 : 1).replace('.', ',') + ' ' + units[i];
}

// ─── Statut de la dernière collecte ───
// `ok` ne s'affiche pas : seul un problème mérite d'être signalé.
const POLL_PROBLEMS = {
  'auth-failed': {
    label: 'Identifiants refusés',
    hint: "Le routeur refuse l'utilisateur ou le mot de passe enregistrés.",
  },
  'hostkey-mismatch': {
    label: 'Clé SSH changée',
    hint: "La clé SSH présentée par le routeur n'est plus celle mémorisée : l'accès est bloqué par prudence.",
  },
  unreachable: {
    label: 'Injoignable',
    hint: "Le serveur RMM n'arrive pas à joindre le routeur (IP, port SSH, pare-feu).",
  },
  'jump-failed': {
    label: 'Rebond injoignable',
    hint: "Le serveur de rebond SSH ne répond pas : l'état du routeur est inconnu.",
  },
  error: { label: 'Erreur de collecte', hint: 'La dernière collecte a échoué.' },
};

/** Problème de collecte du routeur, ou null si tout va bien / pas encore sondé. */
export function pollProblem(device) {
  const s = device?.lastPollStatus;
  if (!s || s === 'ok') return null;
  const p = POLL_PROBLEMS[s] || { label: s, hint: '' };
  return { status: s, ...p, detail: device.lastPollError || '' };
}

/** Message d'échec d'un test de connexion (résultat `MikrotikTestResultDto`). */
export function testFailureMessage(res) {
  const detail = res?.error ? ' (' + res.error + ')' : '';
  switch (res?.status) {
    case 'unreachable':
      return (
        "Injoignable depuis le serveur RMM : vérifier l'IP, le port SSH et /ip service ssh (address=) / le pare-feu du routeur." +
        detail
      );
    case 'jump-failed':
      return (
        "Le serveur de rebond est injoignable ou refuse la connexion : vérifier son adresse, son compte et AllowTcpForwarding." +
        detail
      );
    case 'auth-failed':
      return "Identifiants refusés : vérifier l'utilisateur et le mot de passe." + detail;
    case 'hostkey-mismatch':
      return (
        "La clé SSH présentée n'est pas celle mémorisée pour ce routeur. Si le changement est voulu, utiliser « Accepter la nouvelle clé » sur la fiche." +
        detail
      );
    default:
      return res?.error || 'Échec de la connexion au routeur.';
  }
}

// ─── Titres de commandes propres aux routeurs (cf. MikrotikCommandTypes) ───
export const MIKROTIK_COMMAND_LABELS = { 1: 'Redémarrage', 20: 'Mise à jour RouterOS', 21: 'Sauvegarde' };
export const mikrotikCommandLabel = (c) => MIKROTIK_COMMAND_LABELS[c.type] || 'Commande';

// Format attendu par <input type="datetime-local"> : heure LOCALE, sans fuseau.
export const toLocalInputValue = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const defaultTonight = () => {
  const d = new Date();
  d.setHours(22, 0, 0, 0);
  if (d <= new Date()) d.setDate(d.getDate() + 1);
  return toLocalInputValue(d);
};

/** Pastille « MAJ → x.y.z » / « Firmware » réutilisée par la liste et la fiche. */
export function UpdatePill({ children, title }) {
  return (
    <span
      title={title}
      className="inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold bg-warn/15 text-warn border border-warn/30"
    >
      {children}
    </span>
  );
}
